import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Check, X, BadgePercent, Ban } from "lucide-react";

const STATUS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800", replaced: "bg-slate-100 text-slate-600", revoked: "bg-slate-100 text-slate-600" };
const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export function DiscountsPanel({ students, structures, branchId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "master_admin" || user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ student_id: "", kind: "percent", value: 10, reason: "" });

  const load = () => api.get("/fees/discounts", { params: isAdmin && branchId ? { branch_id: branchId } : {} }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const yearly = (cls) => structures.find((s) => s.class_name === cls)?.yearly_fee || 0;
  const after = (d) => {
    const y = yearly(d.class_name);
    return d.kind === "percent" ? y * (1 - d.value / 100) : null;
  };

  async function submit() {
    try { await api.post("/fees/discounts", { ...f, value: Number(f.value) }); toast.success("Discount request sent for approval"); setOpen(false); setF({ ...f, student_id: "", reason: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function decide(id, status) {
    try { await api.patch(`/fees/discounts/${id}`, { status }); toast.success(`Discount ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function revoke(id) {
    if (!window.confirm("Remove this discount? Future invoices will be charged in full.")) return;
    await api.delete(`/fees/discounts/${id}`); load();
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Each student can have their own discount. Requests are approved by the Super Admin or Master Admin and apply to new invoices.</p>
        <Button data-testid="new-discount-btn" onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-700 font-semibold"><Plus size={16} className="mr-1.5" /> Request discount</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-white py-12 text-center text-slate-500"><BadgePercent className="mx-auto text-violet-300 mb-2" size={32} />No discount requests yet.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-violet-100 divide-y divide-slate-100">
          {rows.map((d) => (
            <div key={d.id} data-testid={`discount-${d.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{d.student_name}</span>
                  <span className="text-xs text-slate-500">{d.admission_number} • {d.class_name} {d.section}</span>
                  <Badge className={`${STATUS[d.status]} capitalize`}>{d.status}</Badge>
                </div>
                <div className="text-sm text-slate-700 mt-1">
                  <b>{d.kind === "percent" ? `${d.value}% off` : `${inr(d.value)} off each invoice`}</b> • {d.reason}
                  {after(d) !== null && yearly(d.class_name) > 0 && <span className="text-slate-500"> • Yearly {inr(yearly(d.class_name))} → <b className="text-emerald-700">{inr(after(d))}</b></span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Requested by {d.requested_by_name} ({ROLE_LABELS[d.requested_by_role]}){d.decided_by ? ` • ${d.status} by ${d.decided_by}` : ""}</div>
              </div>
              {isAdmin && d.status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-discount-${d.id}`} size="sm" onClick={() => decide(d.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" /> Approve</Button>
                  <Button data-testid={`reject-discount-${d.id}`} size="sm" variant="outline" onClick={() => decide(d.id, "rejected")} className="text-rose-600 border-rose-200"><X size={14} className="mr-1" /> Reject</Button>
                </div>
              )}
              {isAdmin && d.status === "approved" && <Button data-testid={`revoke-discount-${d.id}`} size="sm" variant="outline" onClick={() => revoke(d.id)}><Ban size={14} className="mr-1" /> Remove</Button>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl">Request student discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student</Label>
              <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}>
                <SelectTrigger data-testid="discount-student" className="mt-1.5"><SelectValue placeholder="Choose a student" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.class_name} {s.section}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                  <SelectTrigger data-testid="discount-kind" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percent">Percentage (%)</SelectItem><SelectItem value="amount">Fixed ₹ per invoice</SelectItem></SelectContent>
                </Select></div>
              <div><Label>{f.kind === "percent" ? "Percent" : "Amount (₹)"}</Label><Input data-testid="discount-value" type="number" min={1} max={f.kind === "percent" ? 100 : undefined} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Reason</Label><Textarea data-testid="discount-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="mt-1.5" placeholder="Sibling discount, staff child, merit scholarship…" /></div>
            <Button data-testid="discount-submit" onClick={submit} disabled={!f.student_id || !f.reason || Number(f.value) <= 0} className="w-full h-11 bg-violet-600 hover:bg-violet-700">Send for approval</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
